import { Checkbox, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, Input, Select, SelectItem, Tooltip } from '@nextui-org/react';
import React, { useState } from 'react';
import { AiOutlinePlus, AiOutlineDelete, AiOutlineFieldString, AiOutlineFieldNumber } from 'react-icons/ai';
import { MdDataObject, MdDataArray } from "react-icons/md";
import { JSONSchema7 } from "json-schema";
interface TreeNodeData {
    key: string;
    name: string;
    description: string;
    type: 'object' | 'array' | 'string' | 'number' | 'boolean' | 'integer';
    children?: TreeNodeData[];
    value: {};
    required: boolean
}

const TreeNode: React.FC<{
    node: TreeNodeData;
    path: string[];
    onAdd: (path: string[], type: 'object' | 'array' | 'string' | 'number' | 'boolean' | 'integer') => void;
    onDelete: (path: string[]) => void;
    onUpdate: (path: string[], newNode: Partial<TreeNodeData>) => void;
}> = ({ node, path, onAdd, onDelete, onUpdate }) => {
    const [newName, setNewName] = useState<string>(node.name);
    const [newDescription, setNewDescription] = useState<string>(node.description);
    const [newType, setNewType] = useState<typeof node.type>(node.type);
    const [newValue, setNewValue] = useState<string>(String(node.value));
    const [newRequired, setNewRequired] = useState<boolean>(node.required);

    // Handle updating the node properties
    const handleUpdate = () => {
        onUpdate(path, {
            name: newName,
            description: newDescription,
            type: newType,
            required: newRequired,
        });

        // If the new type is a singular type, ensure children are removed
        if (['string', 'number', 'boolean', 'integer'].includes(newType)) {
            onUpdate(path, { children: [] });
        }
    };

    // Generic function to sanitize input and prevent invalid characters
    const sanitizeInput = (value: string, regex: RegExp) => {
        return value.replace(regex, '');
    };

    // Generic function to prevent invalid key presses
    const preventInvalidKey = (e: React.KeyboardEvent, regex: RegExp) => {
        const isControlKey = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(e.key);

        // Allow control keys
        if (isControlKey) return;

        // Allow characters that match the regex
        if (!regex.test(e.key)) {
            e.preventDefault(); // Block the keypress if it doesn't match the allowed characters
        }
    };


    // Call handleUpdate whenever any input changes
    React.useEffect(() => {
        handleUpdate();
    }, [newName, newDescription, newType, newValue, newRequired]);

    return (
        <div className="relative">
            {/* Render the angled bracket line if the node is not the root */}
            {/* Render the 90-degree angle bracket if the node is not the root */}
            {path.length > 0 && (
                <div
                    className="absolute -left-4 -top-2"
                    style={{
                        width: 16,
                        height: '100%',
                    }}
                >
                    {/* Vertical part of the bracket */}
                    <div className="border-l border-blue-300 h-4" style={{ height: '50%' }}></div>
                    {/* Horizontal part of the bracket */}
                    <div className="border-b border-blue-300 w-4"></div>
                </div>
            )}
            <div className="my-2 p-2 border rounded flex-1 Code-editor">
                <div className="flex flex-col gap-2">
                    <div className='flex flex-row gap-2'>
                        <div className='flex-col justify-between'>
                            {newType === 'object' && <MdDataObject size={18} />}
                            {newType === 'array' && <MdDataArray size={18} />}
                            {newType === 'string' && <AiOutlineFieldString size={18} />}
                            {(newType === 'number' || newType === 'integer') && <AiOutlineFieldNumber size={18} />}
                            <Tooltip content="Required">
                                <Checkbox
                                    isSelected={newRequired}
                                    onValueChange={setNewRequired}
                                    size='sm'
                                ></Checkbox>
                            </Tooltip>
                        </div>
                        <Input
                            type="text"
                            isRequired
                            value={newName}
                            onChange={(e) => {
                                // Sanitize input to allow only alphabets and numerals (no spaces)
                                const sanitizedValue = sanitizeInput(e.target.value, /[^a-zA-Z0-9]/g);
                                setNewName(sanitizedValue);
                            }}
                            onKeyDown={(e) => {
                                // Prevent invalid key presses
                                preventInvalidKey(e, /^[a-zA-Z0-9]$/);
                            }}
                            label="Name"
                            labelPlacement='inside'
                        />
                        <Input
                            type="text"
                            value={newDescription}
                            onChange={(e) => {
                                // Sanitize input to allow alphabets, numerals, and spaces
                                const sanitizedValue = sanitizeInput(e.target.value, /[^a-zA-Z0-9 ]/g);
                                setNewDescription(sanitizedValue);
                            }}
                            onKeyDown={(e) => {
                                // Prevent invalid key presses
                                preventInvalidKey(e, /^[a-zA-Z0-9 ]$/);
                            }}
                            label="Description"
                            labelPlacement='inside'
                        />

                        <Select
                            selectedKeys={[newType]}
                            onChange={(e) => setNewType(e.target.value as typeof node.type)}
                            label="Type"
                            labelPlacement='inside'
                        >
                            <SelectItem key="object">Object</SelectItem>
                            <SelectItem key="array">Array</SelectItem>
                            <SelectItem key="string">String</SelectItem>
                            <SelectItem key="number">Number</SelectItem>
                            <SelectItem key="boolean">Boolean</SelectItem>
                            <SelectItem key="integer">Integer</SelectItem>
                        </Select>
                        {/* {(newType === 'string' ||
                            newType === 'number' ||
                            newType === 'boolean' ||
                            newType === 'integer') && (
                                <Input
                                    type="text"
                                    value={newValue}
                                    onChange={(e) => setNewValue(e.target.value)}
                                    label="Default value"
                                    labelPlacement='inside'
                                />
                            )} */}
                        <button className="text-red-500" onClick={() => onDelete(path)}>
                            <AiOutlineDelete size={18} />
                        </button>
                        {(newType === 'object' || newType === 'array') && (
                            <Dropdown>
                                <DropdownTrigger>
                                    <button className="text-red-500">
                                        <AiOutlinePlus size={18} />
                                    </button>

                                </DropdownTrigger>
                                <DropdownMenu aria-label="Static Actions">
                                    <DropdownItem onClick={() => onAdd(path, 'object')} key="object">Add Object </DropdownItem>
                                    <DropdownItem onClick={() => onAdd(path, 'array')} key="array">Add Array []</DropdownItem>
                                    <DropdownItem onClick={() => onAdd(path, 'string')} key="string">Add String</DropdownItem>
                                    <DropdownItem onClick={() => onAdd(path, 'number')} key="number">
                                        Add Number
                                    </DropdownItem>
                                    <DropdownItem onClick={() => onAdd(path, 'boolean')} key="boolean">Add Boolean</DropdownItem>
                                    <DropdownItem onClick={() => onAdd(path, 'integer')} key="integer">Add Integer</DropdownItem>
                                </DropdownMenu>
                            </Dropdown>
                        )}

                    </div>
                </div>
                {node.children && node.children.length > 0 && (
                    <div className="ml-4">
                        {node.children.map((child, index) => (
                            <TreeNode
                                key={child.key}
                                node={child}
                                path={[...path, String(index)]}
                                onAdd={onAdd}
                                onDelete={onDelete}
                                onUpdate={onUpdate}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

interface TreeProps {
    onSchemaChange?: (schema: JSONSchema7) => void;
    schema?: JSONSchema7;
}
const toJSON = (node: TreeNodeData): JSONSchema7 => {
    const { name, description, type, children, value, required } = node;

    const schema: JSONSchema7 = {
        title: name,
        description,
        type,
    };

    // Handle object type: add properties and required fields
    if (type === "object") {
        schema.properties = {};
        const requiredFields: string[] = [];

        if (children) {
            children.forEach((child) => {
                schema.properties![child.name] = toJSON(child);

                // Add child name to requiredFields if child.required is true
                if (child.required) {
                    requiredFields.push(child.name);
                }
            });

            // Only include required fields if there are any
            if (requiredFields.length > 0) {
                schema.required = requiredFields;
            }
        }
    }

    // Handle array type: define the `items` schema
    else if (type === "array") {
        schema.items =
            children && children.length > 0 ? toJSON(children[0]) : {};
    }

    // For singular types like `string`, `number`, `boolean`, `integer`
    else if (["string", "number", "boolean", "integer"].includes(type)) {
        if (value) {
            schema.default = value;
        }
    }

    return schema;
};

const fromJSON = (schema: JSONSchema7, key: string = "root", parentRequired: string[] = []): TreeNodeData => {
    const { title, description, type, properties, items, default: defaultValue, required } = schema;

    // Create the initial TreeNodeData structure
    const node: TreeNodeData = {
        key,
        name: title || key,
        description: description || "",
        type: type as TreeNodeData['type'],
        value: defaultValue || "",
        required: parentRequired.includes(key), // Set required based on the parent's required array
        children: [],
    };

    // Handle object type by converting properties to children nodes
    if (type === "object" && properties) {
        node.children = Object.entries(properties).map(([propName, propSchema]) =>
            fromJSON(propSchema as JSONSchema7, propName, required || [])
        );
    }

    // Handle array type by converting `items` to a child node
    if (type === "array" && items && !Array.isArray(items)) {
        const arrayItemNode = fromJSON(items as JSONSchema7, "items");
        node.children = [arrayItemNode];
    }

    return node;
};

const Tree: React.FC<TreeProps> = ({ onSchemaChange, schema }) => {
    const [treeData, setTreeData] = useState<TreeNodeData>(schema ? fromJSON(schema) : {
        key: 'root',
        name: 'Root',
        description: 'Root Object',
        type: 'object',
        children: [],
        value: {},
        required: true,
    });

    const addNode = (path: string[], type: TreeNodeData['type']) => {
        const newKey = `key_${Date.now()}`;
        const newNode: TreeNodeData = {
            key: newKey,
            name: ``,
            description: '',
            type,
            children: type === 'object' || type === 'array' ? [] : undefined,
            value: type === 'object' ? {} : type === 'array' ? [] : '',
            required: false,
        };

        const updateTree = (node: TreeNodeData, currentPath: string[]): TreeNodeData => {
            if (currentPath.length === 0) {
                return {
                    ...node,
                    children: [...(node.children || []), newNode],
                };
            }

            const [head, ...tail] = currentPath;
            return {
                ...node,
                children: (node.children || []).map((child, index) =>
                    String(index) === head ? updateTree(child, tail) : child
                ),
            };
        };

        setTreeData((prev) => {
            const newTreeData = updateTree(prev, path)
            // Trigger the callback with the updated schema
            if (onSchemaChange) {
                onSchemaChange(toJSON(newTreeData));
            }
            return newTreeData;
        });
    };

    const deleteNode = (path: string[]) => {
        const updateTree = (node: TreeNodeData, currentPath: string[]): TreeNodeData => {
            if (currentPath.length === 0) {
                // Return the node as-is if the path is empty (shouldn't happen in a valid delete operation).
                return node;
            }

            const [head, ...tail] = currentPath;

            if (tail.length === 0) {
                // If at the target level, filter out the child with the matching index.
                return {
                    ...node,
                    children: (node.children || []).filter(
                        (_, index) => String(index) !== head
                    ),
                };
            }

            // If not at the target level, recurse further.
            return {
                ...node,
                children: (node.children || []).map((child, index) =>
                    String(index) === head ? updateTree(child, tail) : child
                ),
            };
        };

        setTreeData((prev) => {
            const newTreeData = updateTree(prev, path)
            // Trigger the callback with the updated schema
            if (onSchemaChange) {
                onSchemaChange(toJSON(newTreeData));
            }
            return newTreeData;
        });
    };

    const updateNode = (path: string[], newNode: Partial<TreeNodeData>) => {
        const updateTree = (node: TreeNodeData, currentPath: string[]): TreeNodeData => {
            if (currentPath.length === 0) {
                return {
                    ...node,
                    ...newNode,
                };
            }

            const [head, ...tail] = currentPath;
            return {
                ...node,
                children: (node.children || []).map((child, index) =>
                    String(index) === head ? updateTree(child, tail) : child
                ),
            };
        };

        setTreeData((prev) => {
            const newTreeData = updateTree(prev, path)
            // Trigger the callback with the updated schema
            if (onSchemaChange) {
                onSchemaChange(toJSON(newTreeData));
            }
            return newTreeData;
        });
    };

    return (
        <div className="py-2 w-full">
            <TreeNode
                node={treeData}
                path={[]}
                onAdd={addNode}
                onDelete={deleteNode}
                onUpdate={updateNode}
            />
        </div>
    );
};

export default Tree;